import { getMedias, linkMedia, unlinkMedia } from '../../../app/api';
import { isImageMimeOrExtension } from '../../medias/public/mediaMime';
import { collectRegisterLineItemImageCandidateIds } from './orderMediaCandidates';

export const REGISTER_LINE_PRODUCT_IMAGE_KEY = 'register_line_product_image';
export const REGISTER_LINE_SKU_IMAGE_KEY = 'register_line_sku_image';

const PRODUCT_IMAGE_KEY = 'product_image';
const SKU_IMAGE_KEY = 'sku_image';

export function buildRegisterLineMediaScope(orderId, formItemId) {
  return `${orderId}:${formItemId}`;
}

function metadataValueMatches(metaValue, scopeValue) {
  if (metaValue == null || scopeValue == null) return false;
  if (typeof metaValue === 'object' && metaValue !== null) {
    return String(metaValue.id) === String(scopeValue);
  }
  return String(metaValue) === String(scopeValue);
}

function mediaMatchesScope(media, metadataKey, metadataValue) {
  if (!media?.metadata?.length) return false;
  return media.metadata.some(
    (meta) => meta.key === metadataKey && metadataValueMatches(meta.value, metadataValue),
  );
}

function resolveMediaOrder(media, metadataKey, metadataValue) {
  const matchingMeta = media.metadata?.find(
    (meta) => meta.key === metadataKey && metadataValueMatches(meta.value, metadataValue),
  );
  if (!matchingMeta) return 0;
  const metaValue = matchingMeta.value;
  if (typeof metaValue === 'object' && metaValue !== null && metaValue.order != null) {
    return Number(metaValue.order) || 0;
  }
  return 0;
}

function collectScopedMedias(allMedias, metadataKey, metadataValue) {
  return allMedias
    .filter((media) => mediaMatchesScope(media, metadataKey, metadataValue))
    .sort(
      (a, b) =>
        resolveMediaOrder(a, metadataKey, metadataValue) - resolveMediaOrder(b, metadataKey, metadataValue),
    );
}

function resolveCatalogMetadataUrl(metadataKey, entityId, productId) {
  if (metadataKey === PRODUCT_IMAGE_KEY && entityId) {
    return `/products/${entityId}/edit`;
  }
  if (metadataKey === SKU_IMAGE_KEY && productId) {
    return `/products/${productId}/edit`;
  }
  return '';
}

function buildScopeMetadataJson(scopeValue, order) {
  return {
    id: scopeValue,
    url: '',
    title: '',
    order,
  };
}

function buildCatalogMetadataJson(entityId, metadataKey, productId, order) {
  return {
    id: entityId,
    url: resolveCatalogMetadataUrl(metadataKey, entityId, productId),
    title: '',
    order,
  };
}

async function linkMediaToScope(mediaId, metadataKey, scopeValue, orderIndex) {
  const metadataJSON = buildScopeMetadataJson(scopeValue, orderIndex);
  await linkMedia(mediaId, {
    [metadataKey]: JSON.stringify(metadataJSON),
  });
}

async function linkMediaToCatalogEntity(mediaId, metadataKey, entityId, productId, orderIndex) {
  const metadataJSON = buildCatalogMetadataJson(entityId, metadataKey, productId, orderIndex);
  await linkMedia(mediaId, {
    [metadataKey]: JSON.stringify(metadataJSON),
  });
}

function resolveUnlinkMetadataValue(metaValue) {
  if (typeof metaValue === 'object' && metaValue !== null && metaValue.id != null && String(metaValue.id).trim() !== '') {
    return String(metaValue.id);
  }
  if (typeof metaValue === 'object' && metaValue !== null) {
    return JSON.stringify(metaValue);
  }
  return metaValue;
}

async function unlinkMediaFromScope(media, metadataKey, scopeValue) {
  const matchingMeta = media.metadata?.find(
    (meta) => meta.key === metadataKey && metadataValueMatches(meta.value, scopeValue),
  );
  if (!matchingMeta) return;
  await unlinkMedia(media.id, metadataKey, resolveUnlinkMetadataValue(matchingMeta.value));
}

async function loadAllMedias() {
  const res = await getMedias();
  return res.data?.medias || [];
}

export async function seedRegisterLineItemCatalogMedias({
  order,
  formItems,
  orderId,
  formItemId,
  formItem,
}) {
  if (!orderId || !formItemId) return false;

  const scopeValue = buildRegisterLineMediaScope(orderId, formItemId);
  const allMedias = await loadAllMedias();
  const candidateIds = collectRegisterLineItemImageCandidateIds(
    order,
    formItems,
    orderId,
    formItem,
    allMedias,
  );
  const byId = new Map(allMedias.map((m) => [m.id, m]));
  const candidates = Array.from(candidateIds)
    .map((id) => byId.get(id))
    .filter((m) => m && isImageMimeOrExtension(m.mime_type, m.filename));

  const existingProduct = collectScopedMedias(allMedias, REGISTER_LINE_PRODUCT_IMAGE_KEY, scopeValue);
  const existingSku = collectScopedMedias(allMedias, REGISTER_LINE_SKU_IMAGE_KEY, scopeValue);
  const existingProductIds = new Set(existingProduct.map((m) => m.id));
  const existingSkuIds = new Set(existingSku.map((m) => m.id));

  const linkTasks = [];
  let productOrder = existingProduct.length;
  let skuOrder = existingSku.length;

  for (const media of candidates) {
    if (!existingProductIds.has(media.id)) {
      linkTasks.push(
        linkMediaToScope(media.id, REGISTER_LINE_PRODUCT_IMAGE_KEY, scopeValue, productOrder),
      );
      productOrder += 1;
    }
    if (!existingSkuIds.has(media.id)) {
      linkTasks.push(linkMediaToScope(media.id, REGISTER_LINE_SKU_IMAGE_KEY, scopeValue, skuOrder));
      skuOrder += 1;
    }
  }

  if (linkTasks.length === 0) {
    return false;
  }

  await Promise.all(linkTasks);
  return true;
}

export async function cleanupRegisterLineItemCatalogMedias({ orderId, formItemId, allMedias: allMediasInput }) {
  if (!orderId || !formItemId) return;

  const scopeValue = buildRegisterLineMediaScope(orderId, formItemId);
  const allMedias = allMediasInput || (await loadAllMedias());

  const productScoped = collectScopedMedias(allMedias, REGISTER_LINE_PRODUCT_IMAGE_KEY, scopeValue);
  const skuScoped = collectScopedMedias(allMedias, REGISTER_LINE_SKU_IMAGE_KEY, scopeValue);
  const seen = new Set();
  const unlinkTasks = [];

  for (const media of [...productScoped, ...skuScoped]) {
    if (seen.has(media.id)) continue;
    seen.add(media.id);
    if (mediaMatchesScope(media, REGISTER_LINE_PRODUCT_IMAGE_KEY, scopeValue)) {
      unlinkTasks.push(unlinkMediaFromScope(media, REGISTER_LINE_PRODUCT_IMAGE_KEY, scopeValue));
    }
    if (mediaMatchesScope(media, REGISTER_LINE_SKU_IMAGE_KEY, scopeValue)) {
      unlinkTasks.push(unlinkMediaFromScope(media, REGISTER_LINE_SKU_IMAGE_KEY, scopeValue));
    }
  }

  if (unlinkTasks.length === 0) {
    return;
  }

  await Promise.all(unlinkTasks);
}

export async function finalizeRegisterLineItemCatalogMedias({ productId, skuId, orderId, formItemId }) {
  if (!productId || !skuId || !orderId || !formItemId) return;

  const scopeValue = buildRegisterLineMediaScope(orderId, formItemId);
  const allMedias = await loadAllMedias();
  const productStaged = collectScopedMedias(allMedias, REGISTER_LINE_PRODUCT_IMAGE_KEY, scopeValue);
  const skuStaged = collectScopedMedias(allMedias, REGISTER_LINE_SKU_IMAGE_KEY, scopeValue);

  const linkTasks = [
    ...productStaged.map((media, index) =>
      linkMediaToCatalogEntity(media.id, PRODUCT_IMAGE_KEY, productId, productId, index),
    ),
    ...skuStaged.map((media, index) =>
      linkMediaToCatalogEntity(media.id, SKU_IMAGE_KEY, skuId, productId, index),
    ),
  ];

  if (linkTasks.length > 0) {
    await Promise.all(linkTasks);
  }

  await cleanupRegisterLineItemCatalogMedias({ orderId, formItemId, allMedias });
}
